import axios from 'axios';

import ApolloAuth from './apolloAuth';
import Session from './session';

// Produção continua sendo o padrão; REACT_APP_API_URL existe para apontar o
// portal a uma API local durante o desenvolvimento.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.duelaser.com',
});

// Uma renovação por vez: a home dispara várias requisições em paralelo e todas
// voltariam 401 juntas. Como a Apolo rotaciona o refresh a cada troca, pedidos
// simultâneos usariam um token já revogado — o que ela trata como vazamento e
// derruba todas as sessões do usuário.
let refreshPromise = null;

function renewSession() {
  if (refreshPromise) return refreshPromise;

  refreshPromise = Session.getRefreshToken()
    .then(refreshToken => {
      if (!refreshToken) throw new Error('Sessão não encontrada');
      return ApolloAuth.refresh({ refreshToken });
    })
    .then(async data => {
      await Session.saveTokens({
        token: data.token,
        refreshToken: data.refreshToken,
      });
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

api.interceptors.request.use(async config => {
  if (config.headers.Authorization) return config;

  const token = await Session.getToken();

  config.headers.Authorization = token ? `Bearer ${token}` : '';

  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;

    if (error.response?.status !== 401 || !original) {
      return Promise.reject(error);
    }

    if (original.skipUnauthorizedRedirect) {
      return Promise.reject(error);
    }

    // Se já tentamos com um token recém-renovado e continuou 401, não é
    // validade: a sessão acabou mesmo.
    if (!original.isRetryAfterRefresh) {
      try {
        const token = await renewSession();

        original.isRetryAfterRefresh = true;
        original.headers.Authorization = `Bearer ${token}`;

        return await api(original);
      } catch {
        // Cai no encerramento de sessão abaixo.
      }
    }

    await Session.clear();
    window.location.href = '/';

    return Promise.reject(error);
  }
);

export { renewSession };
export default api;
